import React, { useState } from 'react';
import { XMarkIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Spinner } from './Spinner';

// Permission modules and their actions
const PERMISSION_MODULES = [
  { key: 'leads', label: 'Leads', actions: ['create', 'read', 'update', 'delete', 'bulkUpload'] },
  { key: 'followups', label: 'Follow-ups', actions: ['create', 'read', 'update', 'delete'] },
  { key: 'conversions', label: 'Conversions', actions: ['create', 'read', 'update', 'delete'] },
  { key: 'reports', label: 'Reports', actions: ['view'] },
  { key: 'dashboard', label: 'Dashboard', actions: ['view'] },
  { key: 'users', label: 'Users', actions: ['create', 'read', 'update', 'delete', 'bulkUpload'] },
];

const ALL_ACTIONS = ['create', 'read', 'update', 'delete', 'view', 'bulkUpload'];
const ACTION_LABELS = { bulkUpload: 'Bulk Upload' };

export const UserPermissionsModal = ({ user, grantorPermissions, grantorRole, onSave, onClose }) => {
  const [permissions, setPermissions] = useState(
    () => JSON.parse(JSON.stringify(user.permissions)) // deep clone
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // A permission checkbox is disabled if the grantor doesn't have it themselves
  const canGrant = (module, action) => {
    return grantorPermissions?.[module]?.[action] === true;
  };

  const handleToggle = (module, action) => {
    if (!canGrant(module, action)) return;
    setPermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [action]: !prev[module][action],
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await onSave(user._id, permissions);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update permissions');
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleName = {
    admin: 'Admin',
    manager: 'Manager',
    sales_executive: 'Sales Executive',
  }[user.role] || user.role;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <ShieldCheckIcon className="h-5 w-5 text-primary-500" />
              <h3 className="text-lg font-medium text-gray-900">
                Manage Permissions — {user.firstName} {user.lastName}
              </h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Role: <span className="font-medium">{roleName}</span>. Grayed-out permissions are not available
            because you don't hold them yourself.
          </p>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 pr-4 font-medium text-gray-700 w-32">Module</th>
                    {ALL_ACTIONS.map(action => (
                      <th key={action} className="text-center py-2 px-3 font-medium text-gray-700 capitalize">
                        {ACTION_LABELS[action] || action}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {PERMISSION_MODULES.map(({ key, label, actions }) => (
                    <tr key={key} className="hover:bg-gray-50">
                      <td className="py-3 pr-4 font-medium text-gray-800">{label}</td>
                      {ALL_ACTIONS.map(action => (
                        <td key={action} className="py-3 px-3 text-center">
                          {actions.includes(action) ? (
                            <input
                              type="checkbox"
                              checked={permissions[key]?.[action] || false}
                              onChange={() => handleToggle(key, action)}
                              disabled={!canGrant(key, action)}
                              className={`h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                                !canGrant(key, action) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
                              }`}
                            />
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-200">
              <button type="button" onClick={onClose} className="btn-outline-secondary" disabled={isSubmitting}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={isSubmitting}>
                {isSubmitting ? <><Spinner size="sm" className="mr-2" />Saving...</> : 'Save Permissions'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
